import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook to keep the session alive and handle token refresh
 */
export const useSessionKeepAlive = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    // Function to check and refresh session
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session check error:', error);
          // If session expired, try to refresh
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            console.error('Failed to refresh session:', refreshError);
            // Redirect to login if refresh fails
            window.location.href = '/login';
          } else if (refreshData.session) {
            console.log('Session refreshed successfully');
          }
        } else if (session) {
          // Check if token is about to expire (within 5 minutes)
          const expiresAt = session.expires_at;
          if (expiresAt) {
            const expiresIn = expiresAt * 1000 - Date.now();
            if (expiresIn < 5 * 60 * 1000) {
              console.log('Token expiring soon, refreshing...');
              await supabase.auth.refreshSession();
            }
          }
        }
      } catch (error) {
        console.error('Session keep-alive error:', error);
      }
    };

    // Initial check
    checkSession();

    // Set up interval to check every 2 minutes
    const interval = setInterval(checkSession, 2 * 60 * 1000);

    // Also check on focus
    const handleFocus = () => {
      console.log('Window focused, checking session...');
      checkSession();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user]);
};